import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { isAuthenticated, setAuth, clearAuth, getRefreshToken, getUserId } from '../auth';
import { API_BASE } from '../config';
import { api, registerSessionExpiredHandler } from '../api';
import { registerFcmToken } from '../services/webPush';
import { identifyUser, trackEvent } from '../services/analytics';

interface AuthContextType {
  isLoggedIn: boolean | null;
  isBanned: boolean;
  login: (token: string, userId: string, refreshToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  handleBanned: () => void;
  dismissBanned: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isLoggedIn: null,
  isBanned: false,
  login: async () => {},
  logout: async () => {},
  handleBanned: () => {},
  dismissBanned: () => {},
});

const BAN_POLL_INTERVAL = 30000;

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [isBanned, setIsBanned] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const handleBanned = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    clearAuth().catch(() => null);
    setIsLoggedIn(false);
    setIsBanned(true);
  }, []);

  const dismissBanned = useCallback(() => {
    setIsBanned(false);
  }, []);

  const checkBanStatus = useCallback(async () => {
    try {
      await api.get('/auth/me');
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? '';
      if (err?.response?.status === 401 && typeof msg === 'string' && msg.toLowerCase().includes('banned')) {
        handleBanned();
      }
    }
  }, [handleBanned]);

  const startPolling = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(checkBanStatus, BAN_POLL_INTERVAL);
  }, [checkBanStatus]);

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const logout = useCallback(async () => {
    stopPolling();
    try {
      const rt = await getRefreshToken();
      if (rt) {
        axios.post(`${API_BASE}/auth/logout`, { refresh_token: rt }, { timeout: 5000 }).catch(() => null);
      }
    } catch { /* ignore */ }
    try { await clearAuth(); } catch { /* ignore */ }
    identifyUser(null);
    trackEvent('logout');
    setIsBanned(false);
    setIsLoggedIn(false);
  }, [stopPolling]);

  useEffect(() => {
    registerSessionExpiredHandler(() => { setIsLoggedIn(false); });
    isAuthenticated().then(authenticated => {
      setIsLoggedIn(authenticated);
      if (authenticated) {
        startPolling();
        registerFcmToken();
        getUserId().then(uid => { if (uid) identifyUser(uid); });
      }
    });
    return () => stopPolling();
  }, [startPolling, stopPolling]);

  const login = async (token: string, userId: string, refreshToken?: string) => {
    await setAuth(token, userId, refreshToken);
    identifyUser(userId);
    trackEvent('login');
    setIsBanned(false);
    setIsLoggedIn(true);
    startPolling();
    registerFcmToken();
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, isBanned, login, logout, handleBanned, dismissBanned }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
